import { ROUTES } from "@/routes/Routes";
import { authenticationStore } from "@/state/auth/AuthStore";
import {
  afterTransactionFieldUpdated,
  setTransactionSignatures,
  setTransactionType,
} from "@/state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import {
  requestCreateTransaction,
  requestUpdateTransaction,
  requestUploadTransactionAttachments,
} from "@/state/transactions/TransactionEffects";
import { transactionSettingsStore } from "@/state/transactions/TransactionSettingsStore";
import {
  ActiveUser,
  AuthenticationContext,
  UserOffice,
} from "@/type/core/auth/auth.types";
import { File, Signature } from "@/type/core/dashboard/dashboard.types";
import { ModelMail } from "@/type/core/models/models.types";
import {
  ActivateSettings,
  Transaction,
  TransactionType,
} from "@/type/core/transaction/transaction.types";
import { MailHelper } from "@/utils/helpers/mail-helper";
import { createTransactionSignatories } from "@/utils/helpers/signatories-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import useOfficesToDisplay from "@/utils/hooks/useOfficesToDisplay";
import { useUnit } from "effector-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface UseTransactionScreenReturn {
  activeBadge: number;
  setActiveBadge: Dispatch<SetStateAction<number>>;
  handleChangeBadge: (badge: number) => void;
  preselectedOfficeId?: string;
  me?: ActiveUser;
  isLoading?: boolean;
  invitation?: ModelMail;
  endOfTransaction?: ModelMail;
  isSignaturesFromModel?: boolean;
  activateAttachment?: boolean;
  attachments: File[];
  signatures: Signature[];
  files: File[];
}

const useTransactionScreen = (
  transactionType: TransactionType,
): UseTransactionScreenReturn => {
  const navigate = useNavigate();

  const { officesToDisplay } = useOfficesToDisplay();

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const transactionSettings = useUnit<ActivateSettings | null>(
    transactionSettingsStore,
  );
  const { t } = useTranslation();
  const {
    transactionForm,
    signatories,
    attachments,
    isSignaturesFromModel,
    isFromDraft,
    transactionDetail,
    signatures,
    files,
  } = useCurrentTransaction();

  const officeChoiceDeactivated = isSignaturesFromModel || isFromDraft;

  const [activeBadge, setActiveBadge] = useState<number>(
    officeChoiceDeactivated ? 2 : 1,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setTransactionType(transactionType);
  }, [transactionType]);

  useEffect(() => {
    return afterTransactionFieldUpdated.watch((eventPayload) => {
      // This means that we come from SignatureMessageSection
      if (
        transactionType === "signature" &&
        eventPayload.length > 0 &&
        eventPayload[0].field === "reference"
      ) {
        const [
          reference,
          endOfTransaction,
          invitation,
          endOfTransactionModelMailId,
        ] = eventPayload;
        handleMessageSubmit(
          reference.value as string,
          endOfTransaction.value as ModelMail,
          invitation.value as ModelMail,
          endOfTransactionModelMailId.value as string,
          officesToDisplay.filter(
            (office) => office.id === transactionForm.office,
          )[0],
        );
      } else if (
        transactionType === "scellement" &&
        eventPayload.length > 0 &&
        eventPayload[0].field === "reference"
      ) {
        const [reference, endOfTransaction, endOfTransactionModelMailId] =
          eventPayload;
        handleMessageSubmit(
          reference.value as string,
          endOfTransaction.value as ModelMail,
          undefined,
          endOfTransactionModelMailId.value as string,
          officesToDisplay.filter(
            (office) => office.id === transactionForm.office,
          )[0],
        );
      }
    });
  });

  const preselectedOfficeId =
    transactionForm.office || authStore?.selectedOfficeIds?.[0];

  const handleChangeBadge = (badge: number) => {
    setActiveBadge(badge + 1);
  };

  const uploadTransactionAttachments = (transactionId: string) => {
    const body = new FormData();
    attachments.forEach((attachment, key) => {
      if (!attachment.file) {
        return;
      }
      body.append("files[]", attachment.file);
      body.append("documentOrders[]", (key + 1).toString());
    });

    requestUploadTransactionAttachments({ dto: body, id: transactionId })
      .then((response) => {
        if (response.ok) {
          navigate(ROUTES.extranet.transactions.positioning.generate());
        } else {
          toastError(t("error.toast.model.attachmentsUpload"));
        }
      })
      .catch(() => {
        toastError(t("error.toast.model.attachmentsUpload"));
      });
  };

  const handleSignatureAfterSubmit = () => {
    if (signatures && signatures.length) {
      let toUpdate = false;
      let filteredSignatures: Signature[] = [];
      if (signatories.length) {
        const signatoriesEmails = signatories.map((s) => s.email);
        filteredSignatures = signatures.filter(
          (s) => s.email && signatoriesEmails.includes(s.email),
        );
        toUpdate = true;
      }
      if (files.length) {
        const documentIds = files.map((f) => f.id);
        filteredSignatures = filteredSignatures.filter(
          (s) => s.documentId && documentIds.includes(s.documentId),
        );
      }
      if (toUpdate) {
        setTransactionSignatures(filteredSignatures);
      }
    }
  };
  const handleMessageSubmit = (
    reference: string,
    endOfTransaction: ModelMail,
    invitation?: ModelMail,
    endOfTransactionModelMailId?: string,
    selectedOffice?: UserOffice,
  ) => {
    setIsLoading(true);

    const dto: Transaction = {
      reference: reference,
      invitationMailConfiguration:
        MailHelper.fromUserMailConfigurationToMailConfiguration(
          invitation?.mailConfiguration,
          selectedOffice,
        ),
      endOfTransactionMailConfiguration:
        MailHelper.fromUserMailConfigurationToMailConfiguration(
          endOfTransaction?.mailConfiguration,
          selectedOffice,
        ),
      signatories: createTransactionSignatories(signatories),
      type: transactionType,
      office: transactionForm.office,
      confidential: transactionForm.confidential,
      authorizedExpertForConfidentiality:
        transactionForm.authorizedExpertForConfidentiality,
      extraRecipients: transactionForm.extraRecipients,
      invitationMode: transactionForm.invitationMode,
      modelTransactionId: transactionForm.modelTransactionId,
      endOfTransactionModelMailId,
    };

    if (isFromDraft && transactionDetail?.id) {
      void requestUpdateTransaction({
        id: transactionDetail.id,
        dto: { ...dto, status: transactionDetail.status },
      })
        .then((response) => {
          if (response.ok && response.data) {
            handleSignatureAfterSubmit();
            if (attachments.length < 1) {
              navigate(ROUTES.extranet.transactions.positioning.generate());
            } else {
              uploadTransactionAttachments(transactionDetail.id);
            }
          } else {
            toastError(
              t("error.toast.transactionUpdate", {
                transactionId: transactionDetail.id,
              }),
            );
          }
        })
        .catch(() => {
          toastError(
            t("error.toast.transactionUpdate", {
              transactionId: transactionDetail.id,
            }),
          );
        })
        .finally(() => setIsLoading(false));
    } else {
      void requestCreateTransaction({ dto })
        .then((response) => {
          if (response.ok && response.data?.id) {
            if (attachments.length < 1) {
              setIsLoading(false);
              navigate(ROUTES.extranet.transactions.positioning.generate());
            } else {
              uploadTransactionAttachments(response.data.id);
            }
          } else {
            toastError(t("error.toast.transactionCreate"));
          }
        })
        .catch(() => {
          toastError(t("error.toast.transactionCreate"));
        })
        .finally(() => setIsLoading(false));
    }
  };

  return {
    activeBadge,
    setActiveBadge,
    handleChangeBadge,
    preselectedOfficeId,
    me: authStore?.user,
    isLoading,
    invitation: transactionForm.invitation,
    endOfTransaction: transactionForm.endOfTransaction,
    isSignaturesFromModel,
    attachments,
    activateAttachment: transactionSettings?.activateAttachment,
    signatures,
    files,
  };
};

export default useTransactionScreen;
